<template>
  <div class="px-4 py-3">
    <div v-if="totalCount === 0" class="text-center text-xs text-growth-400 py-6">
      No attribution data available
    </div>
    <template v-else>
      <div class="flex items-baseline justify-between mb-3">
        <div>
          <div class="text-sm font-semibold text-growth-800">Attribution</div>
          <div class="text-[11px] text-growth-500 mt-0.5">Lives by entity size</div>
        </div>
        <div class="text-[11px] text-growth-500 tabular-nums">
          total
          <span class="text-growth-700 font-semibold">{{ formatLives(totalLives) }}</span>
          <span class="text-growth-400 ml-2">{{ attributedCount }} of {{ totalCount }} entities</span>
        </div>
      </div>
      <div class="flex gap-6 items-stretch">
        <!-- Size bucket donut + legend -->
        <div class="flex items-center gap-4 shrink-0 pr-6 border-r border-gray-200">
          <svg :viewBox="`0 0 ${size} ${size}`" :width="size" :height="size" class="block shrink-0">
            <g :transform="`translate(${size/2}, ${size/2})`">
              <template v-if="bucketDonutSlices.length > 0">
                <path
                  v-for="(slice, i) in bucketDonutSlices"
                  :key="i"
                  :d="slice.path"
                  :fill="slice.color"
                  class="cursor-default transition-opacity duration-150"
                  :opacity="hoveredBucket !== null && hoveredBucket !== i ? 0.5 : 0.85"
                  @mouseenter="hoveredBucket = i"
                  @mouseleave="hoveredBucket = null"
                >
                  <title>{{ slice.label }}: {{ slice.count }} entities ({{ slice.percent }}%)</title>
                </path>
              </template>
              <template v-else>
                <circle :r="(outerRadius + innerRadius) / 2" fill="none" stroke="#e5e7eb" :stroke-width="outerRadius - innerRadius" />
              </template>
            </g>
            <text :x="size/2" :y="size/2 - 6" text-anchor="middle" dominant-baseline="auto"
              class="fill-growth-800" style="font-size: 18px; font-weight: 600;">
              {{ formatLives(totalLives) }}
            </text>
            <text :x="size/2" :y="size/2 + 10" text-anchor="middle" dominant-baseline="auto"
              class="fill-growth-400" style="font-size: 10px;">
              total lives
            </text>
          </svg>
          <div class="flex flex-col gap-1.5">
            <div
              v-for="slice in bucketLegend"
              :key="slice.id"
              class="flex items-center gap-2 cursor-default"
              @mouseenter="hoveredBucket = slice.donutIndex"
              @mouseleave="hoveredBucket = null"
            >
              <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ backgroundColor: slice.color, opacity: 0.85 }"></span>
              <span class="text-[11px] text-growth-700 whitespace-nowrap">
                {{ slice.label }}
                <span class="text-growth-400 ml-1 tabular-nums">({{ slice.count }})</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Top-N entities bars -->
        <div class="flex-1 flex flex-col gap-2 justify-center min-w-0 py-1">
          <div class="text-[11px] font-semibold text-growth-500 uppercase tracking-wider mb-0.5">
            Top {{ topEntities.length }} by attributed lives
          </div>
          <div v-if="topEntities.length === 0" class="text-xs text-growth-400 py-4 text-center">
            No attributed entities in this selection
          </div>
          <div v-for="entity in topEntities" :key="entity.entity_id" class="flex items-center gap-3">
            <div class="sensitive text-xs font-medium text-growth-800 w-44 truncate shrink-0" :title="entity.name">
              {{ entity.name }}
            </div>
            <div class="flex-1 bg-gray-100 rounded h-4 overflow-hidden min-w-0">
              <div
                class="h-4 rounded-l transition-all duration-500 ease-out"
                :style="{ width: Math.max(entity.relPercent, 2) + '%', backgroundColor: '#4C6E97', opacity: 0.75 }"
              />
            </div>
            <div class="text-[11px] text-growth-600 w-28 text-right shrink-0 tabular-nums">
              {{ formatLives(entity.lives) }}
              <span class="text-growth-400">({{ entity.totalPercent }}%)</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
const SIZE_BUCKETS = [
  { id: 'b0',     label: 'No attribution',  color: '#d1d5db', min: null, max: null },
  { id: 'b100',   label: '1–100 lives',     color: '#A3C4EE', min: 1,     max: 100 },
  { id: 'b500',   label: '101–500 lives',   color: '#7799C2', min: 101,   max: 500 },
  { id: 'b2000',  label: '501–2,000 lives', color: '#4C6E97', min: 501,   max: 2000 },
  { id: 'b2001',  label: '2,000+ lives',    color: '#21426C', min: 2001,  max: Infinity },
]

export default {
  name: 'EntityAttributionCoverage',
  props: {
    allEntities: { type: Array, required: true },
    topN: { type: Number, default: 10 }
  },
  data() {
    return {
      hoveredBucket: null,
      size: 150,
      outerRadius: 68,
      innerRadius: 42
    }
  },
  computed: {
    totalCount() {
      return this.allEntities.length
    },
    totalLives() {
      return this.allEntities.reduce((s, e) => s + (Number(e.attributed_lives) || 0), 0)
    },
    attributedCount() {
      return this.allEntities.filter(e => (Number(e.attributed_lives) || 0) > 0).length
    },
    bucketCounts() {
      const counts = {}
      for (const def of SIZE_BUCKETS) counts[def.id] = 0
      for (const e of this.allEntities) {
        const lives = Number(e.attributed_lives) || 0
        if (lives <= 0) { counts.b0 += 1; continue }
        const bucket = SIZE_BUCKETS.find(b => b.min != null && lives >= b.min && lives <= b.max)
        if (bucket) counts[bucket.id] += 1
        else counts.b0 += 1
      }
      return counts
    },
    rawBuckets() {
      const counts = this.bucketCounts
      return SIZE_BUCKETS.map(b => ({
        id: b.id,
        label: b.label,
        color: b.color,
        count: counts[b.id] || 0
      }))
    },
    bucketLegend() {
      let donutIdx = 0
      return this.rawBuckets.map(b => {
        const inDonut = b.count > 0
        const obj = { ...b, donutIndex: inDonut ? donutIdx : null }
        if (inDonut) donutIdx++
        return obj
      })
    },
    bucketDonutSlices() {
      const nonZero = this.rawBuckets.filter(b => b.count > 0)
      const total = nonZero.reduce((s, b) => s + b.count, 0)
      if (total === 0) return []
      let cumulative = 0
      return nonZero.map(b => {
        const fraction = b.count / total
        const start = cumulative
        const end = cumulative + fraction
        cumulative = end
        return {
          ...b,
          percent: Math.round((b.count / this.totalCount) * 100),
          path: this.arcPath(start, end)
        }
      })
    },
    topEntities() {
      const sorted = this.allEntities
        .filter(e => (Number(e.attributed_lives) || 0) > 0)
        .map(e => ({
          entity_id: e.entity_id,
          name: e.name,
          lives: Number(e.attributed_lives) || 0
        }))
        .sort((a, b) => b.lives - a.lives)
        .slice(0, this.topN)
      const max = sorted.length > 0 ? sorted[0].lives : 0
      const totalLives = this.totalLives || 1
      return sorted.map(e => ({
        ...e,
        relPercent: max > 0 ? Math.round((e.lives / max) * 100) : 0,
        totalPercent: Math.round((e.lives / totalLives) * 100)
      }))
    }
  },
  methods: {
    formatLives(n) {
      if (n == null) return '0'
      const v = Number(n)
      if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M'
      if (v >= 10000) return Math.round(v / 1000) + 'K'
      if (v >= 1000) return (v / 1000).toFixed(1) + 'K'
      return v.toLocaleString()
    },
    arcPath(startFraction, endFraction) {
      if (endFraction - startFraction >= 0.9999) {
        return this.fullCirclePath()
      }
      const gap = 0.003
      const s = (startFraction + gap) * 2 * Math.PI - Math.PI / 2
      const e = (endFraction - gap) * 2 * Math.PI - Math.PI / 2
      if (e <= s) return ''
      const largeArc = (endFraction - startFraction) > 0.5 ? 1 : 0
      const oR = this.outerRadius
      const iR = this.innerRadius
      const x1 = oR * Math.cos(s)
      const y1 = oR * Math.sin(s)
      const x2 = oR * Math.cos(e)
      const y2 = oR * Math.sin(e)
      const x3 = iR * Math.cos(e)
      const y3 = iR * Math.sin(e)
      const x4 = iR * Math.cos(s)
      const y4 = iR * Math.sin(s)
      return [
        `M ${x1} ${y1}`,
        `A ${oR} ${oR} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${x3} ${y3}`,
        `A ${iR} ${iR} 0 ${largeArc} 0 ${x4} ${y4}`,
        'Z'
      ].join(' ')
    },
    fullCirclePath() {
      const oR = this.outerRadius
      const iR = this.innerRadius
      return [
        `M 0 ${-oR}`,
        `A ${oR} ${oR} 0 1 1 0 ${oR}`,
        `A ${oR} ${oR} 0 1 1 0 ${-oR}`,
        'Z',
        `M 0 ${-iR}`,
        `A ${iR} ${iR} 0 1 0 0 ${iR}`,
        `A ${iR} ${iR} 0 1 0 0 ${-iR}`,
        'Z'
      ].join(' ')
    }
  }
}
</script>
